import isDesktop from '@lib/is-desktop';
import _throttle from 'lodash/throttle';

type ToggleCallback = (isExpanded: boolean) => void;

class Expandable {
    private _expanded = false;
    private onToggleCallbacks: ToggleCallback[] = [];

    readonly expandBtn: HTMLButtonElement | null;
    readonly collapseBtn: HTMLButtonElement | null;
    readonly toggleBtn: HTMLButtonElement | null;
    readonly expandablePart: HTMLDivElement | null;
    readonly onlyOnBreakpoint: string;

    get expanded(): boolean {
        return this._expanded;
    }
    set expanded(isExpanded: boolean) {
        this._expanded = isExpanded;
        this.onToggleCallbacks.forEach((cb) => {
            try {
                cb(this.expanded);
            } catch (e) {
                setTimeout(() => {
                    throw e;
                }, 100);
            }
        });
    }

    constructor(private readonly root: HTMLDivElement) {
        this.expanded = root.dataset['expanded'] === 'true';

        this.expandBtn = this.root.querySelector<HTMLButtonElement>('[data-expandable-expand]');
        this.collapseBtn = this.root.querySelector<HTMLButtonElement>('[data-expandable-collapse]');
        this.toggleBtn = this.root.querySelector<HTMLButtonElement>('[data-expandable-toggle]');
        this.expandablePart = this.root.querySelector<HTMLDivElement>('[data-expandable-part]');
        this.onlyOnBreakpoint = this.root.dataset['expandable'] || '';

        [this.collapseBtn, this.expandBtn, this.toggleBtn].forEach((toggleBtn) => {
            toggleBtn?.addEventListener('click', () => {
                this.toggle();
            });
        });

        window.addEventListener(
            'resize',
            _throttle(() => this.resize(), 100),
            { passive: true }
        );

        this.resize();
        this.update();

        root.dataset['expandableInitialized'] = 'true';
    }

    toggle(): boolean {
        this.expanded = !this.expanded;
        this.update();
        return this.expanded;
    }

    collapse(): void {
        this.expanded = false;
        this.update();
    }

    expand(): void {
        this.expanded = true;
        this.update();
    }

    addOnToggleListener(cb: ToggleCallback): void {
        this.onToggleCallbacks.push(cb);
    }

    removeOnToggleListener(cb: ToggleCallback): void {
        const index = this.onToggleCallbacks.indexOf(cb);
        if (index !== -1) this.onToggleCallbacks.splice(index, 1);
    }

    private update(): void {
        this.root.dataset['expanded'] = this.expanded.toString();

        if (this.expanded) {
            this.root.classList.add('is-expanded');
            if (this.expandablePart) this.expandablePart.style.height = this.expandablePart.scrollHeight + 'px';
        } else {
            this.root.classList.remove('is-expanded');
            if (this.expandablePart) this.expandablePart.style.height = 0 + 'px';
        }
    }

    private resize(): void {
        if (
            (isDesktop() && this.onlyOnBreakpoint === 'mobile') ||
            (!isDesktop() && this.onlyOnBreakpoint === 'desktop')
        ) {
            this.expandablePart?.style.removeProperty('height');
            this.expand();
        } else {
            this.update();
        }
    }
}

const EXPANDABLE_MAP = new WeakMap<HTMLDivElement, Expandable>();

function initExpandable(expandable: HTMLDivElement): void {
    if (EXPANDABLE_MAP.has(expandable)) return;
    EXPANDABLE_MAP.set(expandable, new Expandable(expandable));
}

function initExpandableGroup(group: HTMLDivElement): void {
    const expandables = Array.from(group.querySelectorAll<HTMLDivElement>('[data-expandable]'))
        .map((el) => EXPANDABLE_MAP.get(el))
        .filter((el: Expandable | undefined): el is Expandable => !!el);
    expandables.forEach((expandable) => {
        expandable.addOnToggleListener((isExpanded) => {
            if (isExpanded) expandables.filter(notEqual(expandable)).forEach((e) => e.collapse());
        });
    });
}

window.Drupal.behaviors['expandable'] = {
    attach: (context) => {
        context.querySelectorAll<HTMLDivElement>('[data-expandable]').forEach((el) => {
            initExpandable(el);
        });

        context.querySelectorAll<HTMLDivElement>('[data-expandable-group]').forEach((group) => {
            initExpandableGroup(group);
        });
    },
};

function notEqual<T>(element: T): (another: T) => boolean {
    return (another) => element !== another;
}
